import React, { useEffect, useState, useRef } from "react";
import axios from "../../Utils/axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  Avatar,
  IconButton,
  TextField,
  Tooltip,
  Paper,
  Fade,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

function ConversationDialog({ open, onClose, conversation, clientData }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
console.log("ConversationDialog Props:", { conversation, clientData });
const currentClientId = clientData;
  const fetchConversation = async () => {
    if (!conversation?.id_conv || !open) return;
    try {
      if (!data) setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_APIURL}/conversation/${conversation.id_conv}`
      );
      setData(res.data);
    } catch (err) {
      console.error("Erreur API:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversation();
  }, [conversation, open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [data?.messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    const payload = {
      id_conv: conversation.id_conv,
      message: newMessage.trim(),
      sender_id: currentClientId
    };
    try {
      setSending(true);
      await axios.post(`${process.env.REACT_APP_APIURL}/message/send`, payload);
      setNewMessage("");
      await fetchConversation(); 
    } catch (err) {
      console.error("Erreur envoi:", err);
    } finally {
      setSending(false);
    }
  };

  const formatDividerDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
    return date.toLocaleDateString([], { day: "numeric", month: "long" });
  };

  if (!open) return null;
const getContextLabel = (context) => {
  const labels = {
    'workshop_session': 'Session Atelier',
    'offre': 'Consultation',
    'reservation': 'Réservation'
  };
  return labels[context] || 'Général';
};
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Fade}
      PaperProps={{
        sx: { borderRadius: "24px", height: "85vh", display: "flex", flexDirection: "column" },
      }}
    >
      {/* HEADER PRINCIPAL AVEC TITRE & RÉSERVATION */}
      <DialogTitle sx={{ p: 0, borderBottom: "1px solid #f0f0f0" }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ bgcolor: "#111827", fontWeight: "bold", width: 45, height: 45 }}>
            {conversation?.id_conv?.toString().charAt(0)}
          </Avatar>
          
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {data?.conversation?.title || `Conversation #${conversation?.id_conv}`}
            </Typography>
            
            {/* Infos de Réservation */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Chip 
                label={`Réservation: ${data?.conversation?.reservation_number || 'N/A'}`} 
                size="small" 
                variant="outlined"
                sx={{ fontSize: "0.7rem", height: "20px", borderColor: "#e4e6eb", color: "text.secondary" }}
              />
              <Typography 
  variant="caption" 
  sx={{ 
    color: "text.disabled", 
    display: "flex", 
    alignItems: "center", 
    gap: 0.5,
    fontWeight: 600 // Un peu plus gras pour la lisibilité
  }}
>
  <InfoOutlinedIcon sx={{ fontSize: 14 }} /> 
  Ref: {getContextLabel(data?.conversation?.context)}
</Typography>
            </Box>
          </Box>

          <IconButton onClick={onClose} sx={{ bgcolor: "#f3f4f6" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", bgcolor: "#fff", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress size={30} sx={{ color: "#111827" }} />
          </Box>
        ) : (
          <>
            {/* ZONE MESSAGES */}
            <Box
              ref={scrollRef}
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                p: 2,
                display: "flex",
                flexDirection: "column",
                bgcolor: "#f9fafb", // Léger gris pour faire ressortir les bulles blanches
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#e5e7eb", borderRadius: "10px" },
              }}
            >
              {data?.messages?.map((msg, index) => {
                const isClient = msg.sender_role === "client";
                const showDateDivider = index === 0 || 
                  new Date(msg.created_at).toDateString() !== new Date(data.messages[index - 1].created_at).toDateString();

                return (
                  <React.Fragment key={msg.id || index}>
                    {showDateDivider && (
                      <Typography sx={{ alignSelf: "center", my: 3, fontSize: "0.7rem", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.5 }}>
                        {formatDividerDate(msg.created_at)}
                      </Typography>
                    )}

                    <Box sx={{ display: "flex", flexDirection: isClient ? "row-reverse" : "row", gap: 1.5, mb: 2, alignItems: "flex-end" }}>
                      <Avatar sx={{ width: 30, height: 30, fontSize: 11, bgcolor: isClient ? "#111827" : "#fff", color: isClient ? "#fff" : "#111827", border: isClient ? "none" : "1px solid #e4e6eb" }}>
                        {msg.sender?.prenom?.charAt(0)}
                      </Avatar>

                      <Box sx={{ maxWidth: "75%", display: "flex", flexDirection: "column", alignItems: isClient ? "flex-end" : "flex-start" }}>
                        <Paper elevation={0} sx={{
                          p: "12px 16px",
                          borderRadius: isClient ? "20px 20px 4px 20px" : "20px 20px 20px 4px",
                          bgcolor: isClient ? "#0084ff" : "#fff",
                          color: isClient ? "#fff" : "#1f2937",
                          boxShadow: isClient ? "0 4px 12px rgba(0,132,255,0.15)" : "0 2px 5px rgba(0,0,0,0.05)",
                          border: isClient ? "none" : "1px solid #f0f0f0"
                        }}>
                          <Typography variant="body2" sx={{ fontSize: "0.9rem", lineHeight: 1.5 }}>{msg.message}</Typography>
                        </Paper>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                          <Typography sx={{ fontSize: "0.65rem", color: "text.disabled" }}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </Typography>
                          {isClient && (msg.is_read ? <DoneAllRoundedIcon sx={{ fontSize: 14, color: "#0084ff" }} /> : <DoneRoundedIcon sx={{ fontSize: 14, color: "text.disabled" }} />)}
                        </Box>
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>

            {/* INPUT MESSAGE */}
            <Box sx={{ p: 2, borderTop: "1px solid #f0f0f0", bgcolor: "#fff" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#f3f4f6", borderRadius: "24px", px: 2, py: 0.6 }}>
                <IconButton size="small"><AttachFileIcon fontSize="small" sx={{ color: "#9ca3af" }} /></IconButton>
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="Écrivez un message..."
                  value={newMessage}
                  disabled={sending}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSend()}
                  InputProps={{ disableUnderline: true, sx: { fontSize: "0.9rem" } }}
                />
                <IconButton 
                  onClick={handleSend} 
                  disabled={!newMessage.trim() || sending}
                  sx={{ 
                    bgcolor: newMessage.trim() ? "#111827" : "transparent",
                    color: newMessage.trim() ? "#fff" : "#bcc0c4",
                    "&:hover": { bgcolor: "#374151" },
                    width: 35, height: 35
                  }}
                >
                  {sending ? <CircularProgress size={18} color="inherit" /> : <SendRoundedIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ConversationDialog;